import { useEffect, useRef, useLayoutEffect } from "react";
import { Helmet } from "react-helmet-async";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { useNavigate } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

export default function LandingPage() {
  const containerRef = useRef(null);
  const navigate = useNavigate();

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(".hero-fade", { opacity: 0 }, {
        opacity: 1, duration: 2, ease: "power2.out", delay: 0.1,
      });

      gsap.utils.toArray(".fade-in").forEach((el, i) => {
        gsap.fromTo(el, { opacity: 0, y: 50 }, {
          opacity: 1, y: 0, duration: 0.8, ease: "power2.out", delay: i * 0.15,
          scrollTrigger: { trigger: el, start: "top 85%", toggleActions: "play none none none" },
        });
      });

      gsap.utils.toArray('[data-animate="featured"]').forEach((el, i) => {
        gsap.fromTo(el, { opacity: 0, y: 40 }, {
          opacity: 1, y: 0, duration: 0.6, ease: "power2.out", delay: i * 0.05,
          scrollTrigger: { trigger: el, start: "top 90%", toggleActions: "play none none none" },
        });
      });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <>
      <Helmet>
        <title>CovePoint Development | Transforming Land Into Community</title>
        <meta
          name="description"
          content="Covepoint Development Group specializes in land acquisition, entitlement, and residential development throughout the Southeast."
        />
        <meta property="og:title" content="CovePoint Development | Transforming Land Into Community" />
        <meta property="og:description" content="Residential, commercial, and multifamily real estate development across the Southeast." />
        <meta property="og:image" content="https://covepoint.com/share.jpg" />
        <meta property="og:url" content="https://covepoint.com" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="CovePoint Development" />
        <meta name="twitter:description" content="Land development across the Southeast." />
        <meta name="twitter:image" content="https://covepoint.com/share.jpg" />
        <link rel="preload" as="image" href="/hero.webp" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "CovePoint Development Group",
            url: "https://covepoint.com",
            logo: "https://covepoint.com/favicons/android-chrome-512x512.png",
            description: "Land development and entitlement company based in Charlotte, NC.",
            sameAs: ["https://www.linkedin.com/company/covepoint"]
          })}
        </script>
      </Helmet>

      <main
        role="main"
        ref={containerRef}
        className="relative font-roboto text-gray-800 overflow-x-hidden min-h-screen"
      >
        <div
          className="absolute inset-0 z-0 pointer-events-none opacity-[0.07]"
          style={{
            backgroundImage: "url('/asfalt-light.png')",
            backgroundRepeat: "repeat",
            backgroundSize: "300px",
          }}
        />

        {/* Hero Section */}
        <header className="relative h-[70vh] sm:h-[90vh] md:h-[90vh] lg:h-[100vh] flex items-start sm:items-start md:items-center justify-center text-white z-10 overflow-hidden pt-10 sm:pt-16 md:pt-0">
          <img
            src="/hero.webp"
            alt="Aerial view of a Southeast residential community developed by CovePoint"
            className="absolute inset-0 w-full h-full object-cover z-0 animate-kenburns-slow"
            style={{
              filter: "contrast(1.2) brightness(1.05) saturate(1.3)",
              transformOrigin: "center center"
            }}
            fetchpriority="high"
          />
          <div className="absolute inset-0 bg-black/40 z-0" />
          <div className="relative z-10 text-center px-6 sm:px-8 max-w-3xl hero-fade pt-30 sm:pt-50 md:pt-0">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.7)" }}>
              Transforming Land Into Community
            </h1>
            <h2 className="sr-only">Land development across the Southeast</h2>
            <p className="text-lg sm:text-xl mb-6" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}>
              Covepoint specializes in residential, commercial, and multifamily real estate development across the Southeast.
            </p>
            <button
              onClick={() => navigate("/projects")}
              aria-label="Explore CovePoint's current and completed real estate development projects"
              className="border-2 border-blue-500 text-white font-semibold text-sm px-6 py-2 rounded transition hover:bg-blue-600 bg-white/10 backdrop-blur-sm"
            >
              Explore Our Projects
            </button>
          </div>
        </header>

        {/* What We Do Section */}
        <section className="py-20 px-4 sm:px-6 bg-[url('/background-1.jpg')] bg-cover bg-center z-10 relative" aria-label="What We Do">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-16 text-[#0B1D42] fade-in">
              What We Do
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 sm:gap-10">
  {[
    {
      title: "Land Sourcing",
      desc: "We uncover high-potential opportunities through proprietary research and strategic analysis—creating exceptional value by aligning land use with emerging market demand.",
    },
    {
      title: "Entitlements",
      desc: "We navigate entitlements with precision through deep understanding of site constraints and regulatory relationships. Our expertise in rezoning and permitting creates high-value land plans that secure timely approvals.",
    },
    {
      title: "Infrastructure Development",
      desc: "We transform raw land into build-ready sites through expert management of infrastructure development. Our hands-on approach ensures on-time, on-budget delivery that exceeds investor expectations.",
    },
    {
      title: "Development Management",
      desc: "We provide third-party oversight and fee development services as trusted partners to landowners, builders, and investors—driving exceptional outcomes from planning through delivery while maximizing value.",
    },
  ].map(({ title, desc }) => (
    <div
      key={title}
      className="fade-in bg-white p-6 rounded-lg shadow-md border border-gray-100 backdrop-blur"
    >
      <h3 className="text-lg sm:text-xl font-semibold text-[#1994f5] mb-2">
        {title}
      </h3>
      <p className="text-gray-700 text-sm sm:text-base">{desc}</p>
    </div>
  ))}
</div>


          </div>
        </section>

        {/* Featured Projects Section */}
        <section className="bg-gray-100 py-20 px-4 sm:px-6 z-10 relative" aria-label="Featured Projects">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-16 text-[#0B1D42] fade-in">
              Featured Projects
            </h2>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8">
              {[
                {
                  name: "Clark Village Townhomes",
                  slug: "clark-village-towhnhomes",
                  image: "/projects/clarkvillage.webp",
                  summary: "193 townhomes in a well-connected Charlotte location.",
                },
                {
                  name: "Forest Lake Townhomes",
                  slug: "forest-lake-townhomes",
                  image: "/projects/forestlake.webp",
                  summary: "90-unit townhome community in Mooresville, NC.",
                },
                {
                  name: "Calvin Creek",
                  slug: "calvin-creek",
                  image: "/projects/calvin.webp",
                  summary: "228 homesites near Mooresville with open space and trails.",
                },
              
              ].map(({ name, slug, image, summary }) => (
                <a
                  key={name}
                  href={`/projects/${slug}`}
                  data-animate="featured"
                  className="bg-white rounded-xl overflow-hidden shadow-md border border-gray-100 transition-transform transform hover:shadow-xl hover:-translate-y-2 hover:scale-[1.015] duration-300 ease-out block"
                >
                  <div className="aspect-[4/3] bg-gray-300">
                    <img
                      src={image}
                      alt={name}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4 text-left">
                    <h3 className="text-lg sm:text-xl font-semibold text-[#0B1D42] mb-2">
                      {name}
                    </h3>
                    <p className="text-sm sm:text-base text-gray-700">{summary}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Call-to-Action Footer */}
        <section className="bg-[#0B1D42] text-white py-16 px-6 text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Ready to Transform Land Together?
            </h2>
            <p className="text-lg sm:text-xl mb-8">
              Let’s partner on your next development opportunity across the Southeast.
            </p>
            <a
              href="/contact"
              className="inline-block bg-[#1994f5] hover:bg-white hover:text-[#0B1D42] text-white font-semibold px-8 py-4 rounded shadow transition duration-300"
            >
              Contact Us Today
            </a>
          </div>
        </section>
      </main>
    </>
  );
}
